import { Component } from '@angular/core';
import { CompositeFilterDescriptor, filterBy, GroupDescriptor } from '@progress/kendo-data-query';
import { GridViewManager } from 'src/app/core/model/grid-view-manager';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { AppConfigService } from 'src/app/core/service/app-config.service';
import { SearchHelperService } from 'src/app/core/utility/search-helper.service';
import { NGXLogger } from 'ngx-logger';
import { DataStateChangeEvent } from '@progress/kendo-angular-grid';
import { TeamRoleEdit } from '../../model/team-role';
import { TeamService } from 'src/app/core/service/team.service';

@Component({
  standalone: false,
  selector: 'app-team-role-list',
  templateUrl: './team-role-list.component.html',
  styleUrls: ['./team-role-list.component.scss']
})
export class TeamRoleListComponent
{
  public pageSizes = [5, 10, 25, 50, 100];
  public view: GridViewManager;
  public deleteDialogOpened = false;
  public enableAdvanced = false;
  public count: number;
  public isNew = false;
  public editDataItem: TeamRoleEdit;
  public selectedTeamRole: TeamRoleEdit;
  public filterText = '';

  /**
   * Constructor
   */
  constructor(
    private teamRoleService: TeamService,
    private logger: NGXLogger,
    private messageHandlerService: MessageHandlerService,
    private appConfigService: AppConfigService,
    private searchHelperService: SearchHelperService
  )
  {
    this.view = new GridViewManager(this.appConfigService.getConfig().pageSize);
    this.loadGridData();
  }

  /**
   * Closes a component
   *
   * @param component Name of component to be closed.
   */
  public close(component: string): void
  {
    this[component + 'Opened'] = false;
  }

  /**
   * Opens a component
   *
   * @param component Name of component to be opened.
   */
  public open(component: string): void
  {
    this[component + 'Opened'] = true;
  }

  /**
   * Button action handler.
   *
   * @param confirm Confirm the action: yes/no
   */
  public deleteAction(confirm: boolean): void
  {
    if (confirm)
    {
      // Proceed with the deletion
      const teamRoleId = this.selectedTeamRole.id;
      this.teamRoleService.deleteTeamRole(teamRoleId).subscribe({
        next: () =>
        {
          this.view.data = this.view.data.filter(e => e.id !== teamRoleId);
          this.view.refreshView();
          this.count = this.view.data.length;
          this.messageHandlerService.displaySuccess('TEAM-ROLE-DELETED-SUCCESSFULLY');
        },
        error: (errorResponse) =>
        {
          this.messageHandlerService.displayError(errorResponse);
        }
      });
    }
    // post-deletion cleanup and refresh grid
    this.close('deleteDialog');
    this.selectedTeamRole = null;
  }

  /**
   * Data change event handler.
   *
   * @param state State of grid operations
   */
  public dataStateChange(state: DataStateChangeEvent): void
  {
    this.view.setState(state);
  }

  /**
   * Group change event handler.
   *
   * @param groups List of GroupDescriptors
   */
  public groupChange(groups: GroupDescriptor[]): void
  {
    this.view.setGroup(groups);
  }

  /**
   * Sets the user to delete, and opens the delete user dialog.
   */
  public deleteHandler(id: number): void
  {
    // Check if in use
    this.teamRoleService.existsTeamRole(id).subscribe({
      next: (inUseResponse) =>
      {
        // If in use, avoid deletion
        if (inUseResponse)
        {
          this.messageHandlerService.displayWarning('TEAM-ROLE-IN-USE-CANNOT-DELETE');
        }
        else
        {
          this.teamRoleService.getTeamRoleById(id).subscribe({
            next: (data) =>
            {
              this.selectedTeamRole = data as TeamRoleEdit;
              this.open('deleteDialog');
            },
            error: (errorResponse) =>
            {
              this.messageHandlerService.displayError(errorResponse);
            }
          });
        }
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  public addHandler(): void
  {
    this.isNew = true;
    this.editDataItem = new TeamRoleEdit();
  }

  public editHandler(id: number): void
  {
    this.isNew = false;
    this.teamRoleService.getTeamRoleById(id).subscribe({
      next: (data) =>
      {
        this.editDataItem = data;
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  public saveChangesHandler(teamRole: TeamRoleEdit): void
  {
    this.teamRoleService.saveTeamRole(teamRole, this.isNew, teamRole.id).subscribe({
      next: (data) =>
      {
        // Must convert the lastModifiedDate into Date object
        data.lastModifiedDate = new Date(data.lastModifiedDate);
        this.view.addOrUpdateGridRow(data);
        this.count = this.view.data.length;  // Update count

        this.logger.info('saved', teamRole);

        this.editDataItem = undefined;
        this.messageHandlerService.displaySuccess('TEAM-ROLE-SAVED-SUCCESSFULLY');
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  public cancelChangesHandler(): void
  {
    this.editDataItem = undefined;
  }

  /**
   * Filtering by search field
   */
  public filterChange(filter: CompositeFilterDescriptor): void
  {
    this.view.state.filter = filter;
    this.view.gridData.data = filterBy(this.view.data, filter);
    this.count = this.view.gridData.data.length;
    this.view.refreshView();
  }

  public searchFieldChange(filterText: string): void
  {
    this.view.state.skip = 0;
    const root = this.searchHelperService.fieldChange(filterText, this.view.state);
    this.filterText = filterText;
    this.filterChange(root);
  }

  private loadGridData(): void
  {
    this.view.startLoading();
    this.teamRoleService.getTeamRolesList().subscribe({
      next: (data) =>
      {
        data.forEach(d => { d.lastModifiedDate = new Date(d.lastModifiedDate); });
        this.count = data.length;
        this.view.setData(data);
        this.view.setSort([{ field: 'name', dir: 'asc' }]);
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      },
      complete: () =>
      {
        this.view.stopLoading();
      }
    });
  }
}
